import React from 'react'

import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'

import { DATE_FORMAT_OPTIONS, TIME_FORMAT_OPTIONS } from '@/lib/constants'
import { DeviceStatus as DeviceStatusType } from '@/lib/types'

export const DeviceStatus = ({
  status,
  preorderFor,
  nextUpdateAt
}: {
  preorderFor?: Date | null
  status?: DeviceStatusType
  nextUpdateAt: string | undefined
}) => {
  const { formatDate } = useIntl()

  if (preorderFor === undefined && !status) return

  const statusDescription = getStatusDescription(status, preorderFor)
  const isGreenLabel = getStatusLabel(status, preorderFor)

  return (
    <div
      className={clsx(
        'inline-flex rounded-md px-2 py-1 text-[11px]',
        'font-medium leading-[1.27] tracking-[-0.24px]',
        isGreenLabel && 'bg-green-100 text-green-600',
        !isGreenLabel && 'bg-amber-250 text-amber-500'
      )}
    >
      <FormattedMessage
        {...{
          id: statusDescription.id,
          defaultMessage: statusDescription.defaultMessage,
          values: {
            ...(preorderFor && {
              date: formatDate(preorderFor, DATE_FORMAT_OPTIONS)
            }),
            ...(nextUpdateAt && {
              time: formatDate(nextUpdateAt, TIME_FORMAT_OPTIONS)
            })
          }
        }}
      />
    </div>
  )
}

const getStatusLabel = (
  status?: DeviceStatusType,
  preorderFor?: Date | null
) => {
  const { inStock, isOffline, isTurningOn, isTurningOff } = getStatusFlags(
    status,
    preorderFor
  )

  if (!status) {
    return inStock
  }

  if (isTurningOn || isTurningOff) {
    return false
  }

  return !isOffline && inStock
}

const getStatusDescription = (
  status?: DeviceStatusType,
  preorderFor?: Date | null
) => {
  const { inStock, isOffline, isTurningOn, isTurningOff } = getStatusFlags(
    status,
    preorderFor
  )

  const statusMessages = new Map([
    [isOffline, { id: 'device.status.disabled', defaultMessage: 'Отключен' }],
    [
      !inStock,
      {
        id: 'farm.availability.preorder',
        defaultMessage: 'Начнет работу с {date}'
      }
    ],
    [
      isTurningOn,
      {
        id: 'device.status.turningOn',
        defaultMessage: 'Будет включен в {time}'
      }
    ],
    [
      isTurningOff,
      {
        id: 'device.status.turningOff',
        defaultMessage: 'Будет выключен в {time}'
      }
    ]
  ])

  const availabilityMessages = new Map([
    [
      inStock,
      { id: 'shop.filter.availability.inStock', defaultMessage: 'В наличии' }
    ],
    [
      !inStock,
      {
        id: 'shop.availability.preorder',
        defaultMessage: 'Предзаказ на {date}'
      }
    ]
  ])

  const statusDescription = statusMessages.get(true) || {
    id: 'device.status.online',
    defaultMessage: 'Онлайн'
  }
  const availabilityDescription = availabilityMessages.get(true) || {
    id: 'shop.availability.preorder',
    defaultMessage: 'Предзаказ на {date}'
  }

  return status ? statusDescription : availabilityDescription
}

const getStatusFlags = (
  status?: DeviceStatusType,
  preorderFor?: Date | null
) => {
  const inStock = preorderFor === null
  const isOffline = status === 'offline'
  const isTurningOn = status === 'turning_on'
  const isTurningOff = status === 'turning_off'

  return { inStock, isOffline, isTurningOn, isTurningOff }
}
